import { Level } from 'types/models/event';
import {
  EventReason,
  EventReasonCatalogByTriggerType,
  EventReasonPromptContext
} from 'types/models/event-reason';
import Notification, { NotificationEventType, SamplePointEvent } from 'types/models/notification';
import { EventReasonPromptPreference } from 'types/models/user-preference';
import { TriggerType } from 'types/trigger.enum';

const ELIGIBLE_TRIGGER_TYPES = new Set<TriggerType>([
  TriggerType.WATER_TANK_LOW,
  TriggerType.TROUGH_LOW
]);

export function isEligibleForEventReasonPrompt(notification: Notification): boolean {
  if (notification.eventType !== NotificationEventType.SAMPLE_POINT_EVENT) {
    return false;
  }
  const samplePointEvent = notification.event as SamplePointEvent;
  if (samplePointEvent.level !== Level.CRITICAL) {
    return false;
  }
  const triggerType = samplePointEvent.trigger?.triggerType;
  if (!triggerType || !ELIGIBLE_TRIGGER_TYPES.has(triggerType)) {
    return false;
  }
  return true;
}

export function isEventReasonPromptSnoozed(
  preference: EventReasonPromptPreference | undefined,
  nowMs: number = Date.now()
): boolean {
  if (!preference?.snoozedUntil) {
    return false;
  }
  return nowMs < preference.snoozedUntil;
}

export function buildEventReasonPromptContext(notification: Notification): EventReasonPromptContext {
  const { eventType, eventId } = notification;
  if (eventType === NotificationEventType.SAMPLE_POINT_EVENT) {
    const triggerType = (notification.event as SamplePointEvent).trigger?.triggerType;
    if (!triggerType) {
      throw new Error('Sample point notification is missing triggerType');
    }
    return {
      eventType: NotificationEventType.SAMPLE_POINT_EVENT,
      eventId,
      triggerType
    };
  }
  if (eventType === NotificationEventType.IP_CAMERA_EVENT) {
    return {
      eventType: NotificationEventType.IP_CAMERA_EVENT,
      eventId
    };
  }
  throw new Error(`Unsupported event type for event reason prompt: ${eventType}`);
}

export function buildPendingEventReasonTaggingId(
  promptContext: EventReasonPromptContext
): string {
  return `${promptContext.eventType}:${promptContext.eventId}`;
}

export function partitionEventReasonsByTriggerType(
  reasons: EventReason[]
): EventReasonCatalogByTriggerType {
  return reasons.reduce<EventReasonCatalogByTriggerType>((acc, reason) => {
    const existing = acc[reason.triggerType] ?? [];
    return {
      ...acc,
      [reason.triggerType]: [...existing, reason]
    };
  }, {});
}